<template>
	<BaseVcxLayout>
		<!-- hero -->
		<section
			v-scrolled-into-view="MXP_SIV_HERO"
			:data-context="MXP_SIV_HERO['View ID']"
			class="vcx-section vcx-theme-dark vcx-hero"
		>
			<img :src="heroImageUrl" alt="" class="vcx-hero__image" fetchpriority="high" aria-hidden="true" />
			<div class="vcx-container vcx-hero__container">
				<div class="vcx-grid">
					<div
						class="vcx-col-12 vcx-col-8-md vcx-col-offset-2-md vcx-col-6-lg vcx-col-offset-3-lg vcx-text-center vcx-pb-100"
					>
						<h1 class="vcx-h0 vcx-h2-smo vcx-mb-150 vcx-mb-100-smo">
							The public ticker <br v-if="isMobile" />for private&nbsp;tech<sup
								class="vcx-hero__superscript vcx-body-lg vcx-body-sm-smo vcx-font-weight-regular"
								>SM</sup
							>
						</h1>
						<p class="vcx-body-xl vcx-body-md-smo vcx-text-secondary vcx-mb-400 vcx-mb-225-smo">
							VCX is a NYSE-listed public venture fund that gives you access to top private tech
							companies.
						</p>
						<VcxLeadCapture />
					</div>
				</div>
			</div>
		</section>
		<VcxLandingDemocratizingSection
			v-scrolled-into-view="MXP_SIV_DEMOCRATIZING"
			:data-context="MXP_SIV_DEMOCRATIZING['View ID']"
		/>
		<section
			id="about-vcx"
			v-scrolled-into-view="MXP_SIV_ABOUT_VCX"
			:data-context="MXP_SIV_ABOUT_VCX['View ID']"
			class="vcx-section vcx-theme-dark"
		>
			<div class="vcx-container">
				<h2 class="vcx-h2">About VCX</h2>
				<div class="vcx-grid vcx-mt-225 vcx-mt-500-md">
					<div v-if="!isMobile" data-test="vcx-about-overview" class="vcx-col-12 vcx-col-7-md">
						<h3 class="vcx-h5 vcx-mb-200">Overview</h3>
						<p class="vcx-text-secondary vcx-body-lg">
							The Fundrise Innovation Fund (Ticker: VCX) is designed to deliver long-term growth from a
							diversified portfolio of technology companies. The fund is focused on investing in
							potentially high-growth private technology businesses.
						</p>
						<p class="vcx-text-secondary vcx-body-lg">
							VCX takes a “multi-stage” approach, investing across the lifecycle of a private company –
							from early to late stage and continuing to hold after any potential IPOs. The fund invests
							across thematic sectors including artificial intelligence (AI) and machine learning (ML),
							data infrastructure, vertical and horizontal software, and other categories with attractive
							long-term growth potential.
						</p>
						<p>
							<BaseLink
								class="vcx-link"
								:link="{ router: 'home', text: 'Learn more about Fundrise' }"
							></BaseLink>
						</p>
					</div>
					<div class="vcx-col-12 vcx-col-5-md">
						<div class="vcx-card vcx-card-dark">
							<div class="vcx-label-md vcx-text-tertiary">Fund Details</div>
							<table class="vcx-about-the-fund-table vcx-body-lg vcx-mt-100">
								<tbody>
									<tr>
										<td>Ticker</td>
										<td class="vcx-text-right">VCX</td>
									</tr>
									<tr>
										<td>Listing venue</td>
										<td class="vcx-text-right">NYSE</td>
									</tr>
									<tr>
										<td>Advisor</td>
										<td class="vcx-text-right">Fundrise Advisors</td>
									</tr>
									<tr>
										<td>Inception date</td>
										<td class="vcx-text-right">July 2022</td>
									</tr>
									<tr>
										<td>Annual management fee</td>
										<td class="vcx-text-right">1.85%</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
				<p v-if="isMobile" class="vcx-mt-200">
					<BaseLink class="vcx-link" :link="{ router: 'home', text: 'Learn more about Fundrise' }"></BaseLink>
				</p>
			</div>
		</section>
		<!-- performance -->
		<section
			id="performance"
			v-scrolled-into-view="MXP_SIV_PERFORMANCE"
			:data-context="MXP_SIV_PERFORMANCE['View ID']"
			class="vcx-section vcx-theme-dark"
		>
			<div class="vcx-container">
				<h2 class="vcx-h1 vcx-mb-225 vcx-mb-500-md">Performance</h2>
				<VcxPerformanceSection
					:is-loading="isSanityLoading"
					:performance-metrics="vcxPerformanceMetrics"
					:performance-disclaimer="vcxPerformanceDisclaimer"
				/>
			</div>
		</section>
		<VcxLandingNewsroom
			v-scrolled-into-view="MXP_SIV_NEWSROOM"
			:data-context="MXP_SIV_NEWSROOM['View ID']"
			:articles="vcxFeaturedArticles"
			:podcasts="vcxFeaturedPodcasts"
		/>
		<!-- literature -->
		<section
			v-scrolled-into-view="MXP_SIV_LITERATURE"
			:data-context="MXP_SIV_LITERATURE['View ID']"
			class="vcx-section vcx-theme-dark"
		>
			<div class="vcx-container">
				<h2 class="vcx-h4 vcx-mb-100">Literature</h2>
				<p class="vcx-body-lg vcx-text-secondary vcx-mb-400">
					All investors should consider the investment objectives, risks and charges and expenses of the Fund
					carefully before investing. Information regarding such considerations, including the prospectus of
					the ‘40 Act registered fund may be found below. Investors should read the prospectus carefully
					before investing.
				</p>
				<BasePlaceHolder :placehold="isLoading" :section-count="4" :line-count="2" theme="dark">
					<VcxLiteratureLinkGrid class="display-block" :literature-list="vcxLiteratureItems" />
				</BasePlaceHolder>
			</div>
		</section>
	</BaseVcxLayout>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed, provide, Ref, ref } from 'vue';
import VcxLandingDemocratizingSection from '@components/vcx/vcx-landing-democratizing-section.vue';
import VcxLandingNewsroom from '@components/vcx/vcx-landing-newsroom.vue';
import VcxLeadCapture from '@components/vcx/vcx-lead-capture.vue';
import VcxLiteratureLinkGrid from '@components/vcx/vcx-literature-link-grid.vue';
import VcxPerformanceSection from '@components/vcx/vcx-performance-section.vue';
import { appCloudfrontPath } from '@constants';
import { growthTechReitId } from '@constants/reit';
import {
	MXP_SIV_ABOUT_VCX,
	MXP_SIV_DEMOCRATIZING,
	MXP_SIV_HERO,
	MXP_SIV_LITERATURE,
	MXP_SIV_NEWSROOM,
	MXP_SIV_PERFORMANCE
} from '@constants/vcx';
import { getVcxLandingPage } from '@sanity/graphql/vcx-landing-page';
import { getFund } from '@sanity/proxy/funds';
import { useAppStore } from '@stores/app';
import { Fund, FundLiterature } from 'types/funds';
import { SanityKeyValueData } from 'types/sanity/charts-data';
import { SanityFeaturedPodcast, VcxArticleTeaser } from 'types/sanity/vcx-landing-page';

const metaObject = {
	title: 'VCX by Fundrise',
	titleTemplate: null,
	meta: [
		{ property: 'og:title', content: 'VCX by Fundrise' },
		{
			name: 'description',
			content: `VCX: The public ticker for private tech`
		},
		{
			property: 'og:description',
			content: `VCX: The public ticker for private tech`
		},
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: 'https://getvcx.com/' },
		{
			property: 'og:image',
			content: `${appCloudfrontPath}/images/vcx/vcx-meta.png`
		}
	],
	script: [`window.prerenderReady = false;`]
};

const headControl = useHead(metaObject);

const appStore = useAppStore();
const isLoading = ref(true);
const isSanityLoading = ref(true);
provide('isVcxSanityLoading', isSanityLoading);

const vcxPerformanceMetrics: Ref<Array<SanityKeyValueData>> = ref([]);
const vcxPerformanceDisclaimer: Ref<string> = ref('');
const vcxFeaturedArticles: Ref<Array<VcxArticleTeaser>> = ref([]);
const vcxFeaturedPodcasts: Ref<Array<SanityFeaturedPodcast>> = ref([]);
const vcxLiteratureItems: Ref<Array<FundLiterature>> = ref([]);

const heroImageUrl = computed(() => `${appCloudfrontPath}/images/vcx/vcx-hero.webp`);
const isMobile = computed(() => appStore.isMobile);

const fund: Ref<Fund | null> = ref(null);

async function fetchSanityContent() {
	try {
		const vcxLandingPage = await getVcxLandingPage();
		if (vcxLandingPage) {
			vcxPerformanceMetrics.value = vcxLandingPage.vcxPerformanceData;
			vcxPerformanceDisclaimer.value = vcxLandingPage.vcxPerformanceDisclaimer ?? '';
			vcxFeaturedArticles.value = vcxLandingPage.vcxFeaturedArticles;
			vcxFeaturedPodcasts.value = vcxLandingPage.vcxFeaturedPodcasts;
		}
	} finally {
		isSanityLoading.value = false;
	}
}

async function fetchFundData() {
	try {
		fund.value = await getFund(growthTechReitId, {
			include: ['SIMULATED_RETURNS']
		});
		vcxLiteratureItems.value = fund.value?.literature ?? [];
	} finally {
		isLoading.value = false;
	}
}

function setup() {
	Promise.all([fetchSanityContent(), fetchFundData()]).finally(() => {
		headControl?.patch({
			...metaObject,
			script: [`window.prerenderReady = true;`]
		});
	});
}

setup();
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

table.vcx-about-the-fund-table {
	width: 100%;
	tbody {
		tr td {
			padding: var(--vcx-space-100) 0;
		}
		tr:first-of-type td {
			padding-top: var(--vcx-space-50);
		}
		tr:last-of-type td {
			padding-bottom: var(--vcx-space-50);
		}
	}

	tr + tr {
		border-top: 1px solid var(--vcx-content-secondary);
	}
}

.vcx-hero {
	position: relative;
	padding-bottom: 5rem;

	@include vcx-media-smo {
		padding-top: var(--vcx-space-600);
		padding-bottom: var(--vcx-space-600);
	}

	@include vcx-media-lg {
		padding-bottom: 10rem;
	}

	&__superscript {
		top: -1.6rem;

		@include vcx-media-smo {
			top: -0.75rem;
		}
	}

	&__image {
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translate(-50%, 35%);
		width: 100%;
		height: 40vh;
		object-fit: cover;
		object-position: bottom center;
		pointer-events: none;
		z-index: 0;

		@include vcx-media-xl {
			height: 80vh;
		}

		@include vcx-media-xxxl {
			height: 90vh;
			object-position: bottom center;
		}
	}

	&__container {
		position: relative;
		z-index: 1;
	}
}

</style>
